import {useState, useRef, useEffect} from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import moment from 'moment';

import {formatTimestampForScheduledMail} from 'nutshell-core/date-time/formatting';

import {DropdownButton} from 'shells/button/dropdown-button/dropdown-button';
import type {CannedOption} from 'shells/date-time-picker/canned-options';
import {DateTimePickerPopover} from 'shells/date-time-picker/date-time-picker-popover';

import {DataFieldWrapper} from '../../../core-entity/widgets/summary-widget/data-field-wrapper';

import styles from './editable-date.module.css';

interface Props {
    // This is the type we get from GraphQL
    value: any | null | undefined;
    defaultDate?: Date;
    emptyText?: string;
    onUpdate: (arg1?: Date | null | undefined) => void;
    title: string;
    minDate?: Date;
    maxDate?: Date;
    warningText?: string;
    cannedOptions?: CannedOption[];
    saveDateOnYearChange?: boolean;
    isClearable?: boolean;
}

export function EditableDate(props: Props) {
    const {
        value,
        defaultDate,
        emptyText,
        onUpdate,
        title,
        minDate,
        maxDate,
        warningText,
        cannedOptions,
        saveDateOnYearChange,
        isClearable = false,
    } = props;
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

    const closeDateRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    const formattedCloseDate = value ? formatTimestampForScheduledMail(value) : emptyText || '';

    const canClearDate = isClearable && value;

    const styleNames = classnames(styles['clickable-date'], {
        [styles['warning-date']]: Boolean(warningText),
    });

    return (
        <DataFieldWrapper
            label={title}
            children={
                <>
                    <div
                        className={canClearDate ? styles['container-clearable'] : styles.container}
                    >
                        <div
                            ref={closeDateRef}
                            data-tip={
                                warningText ||
                                (value ? moment.unix(value).format('MMMM Do, YYYY h:mm a') : null)
                            }
                            data-place='bottom'
                            className={styleNames}
                        >
                            <DropdownButton
                                label={formattedCloseDate || ''}
                                onClick={() => {
                                    setShowDatePicker(true);
                                }}
                            />
                        </div>
                    </div>
                    {closeDateRef.current && showDatePicker && (
                        <DateTimePickerPopover
                            anchor={closeDateRef.current}
                            value={value ? new Date(value * 1000) : defaultDate || new Date()}
                            onCancel={() => {
                                setShowDatePicker(false);
                            }}
                            onConfirm={(updatedValue) => {
                                onUpdate(updatedValue);
                                setShowDatePicker(false);
                            }}
                            minDate={minDate}
                            maxDate={maxDate}
                            cannedOptions={cannedOptions}
                            saveDateOnYearChange={saveDateOnYearChange}
                            onClear={
                                isClearable && value
                                    ? () => {
                                          onUpdate(null);
                                          setShowDatePicker(false);
                                      }
                                    : undefined
                            }
                        />
                    )}
                </>
            }
        />
    );
}
